import { useDecision } from '@optimizely/react-sdk';
import DynamicForm, { FormValuesOutput } from '@policygenius/mortar-dynamic-forms';
import { useCreateDataPointValues, useGetDataPointValues } from 'api/hooks/useDataPointValues';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { completedNavigatorStep } from 'analytics/navigator';
import { StepName } from 'analytics/navigator/constants';
import DynamicFormWrapper from 'components/DynamicFormWrapper';
import GlobalLoader from 'components/GlobalLoader';
import NavigatorContent from 'components/NavigatorContent';
import { APPLICATION_FULL_PATH, CONTINUE_ONLINE_TRAVEL_PATH } from 'routes/constants';
import useSelector from 'store/useSelector';
import { Flags } from 'util/optimizely/types';

import schema from './schema';
import { FormValues } from './types';

const INTERVIEW_URNS: Array<keyof FormValues> = [
  'urn:pg:data-point:insured-risk-activity-bankruptcy',
  'urn:pg:data-point:insured-risk-activity-bankruptcy-chapter-filed',
  'urn:pg:data-point:insured-risk-activity-bankruptcy-filed-date',
  'urn:pg:data-point:insured-risk-activity-bankruptcy-discharged-date',
  'urn:pg:data-point:insured-risk-activity-bankruptcy-reason',
  'urn:pg:data-point:insured-wage-replacement-history',
  'urn:pg:data-point:insured-adverse-insurance-history',
  'urn:pg:data-point:insured-adverse-insurance-history-coverage-type',
  'urn:pg:data-point:insured-adverse-insurance-history-date',
  'urn:pg:data-point:insured-adverse-insurance-history-reason',
  'urn:pg:data-point:insured-subject-to-backup-withholding',
];

const Finances: React.FC = () => {
  const navigate = useNavigate();
  const activeUserUuid = useSelector((app) => app.activeUserUuid);
  const [{ enabled: funnelRemixP2 }] = useDecision(Flags.LIFE_FUNNEL_REMIX_P2);

  const { data, loading } = useGetDataPointValues({
    params: {
      urns: INTERVIEW_URNS,
    },
  });

  const { createDataPointValuesFromInterview } = useCreateDataPointValues({
    onCompleted: () => {
      completedNavigatorStep(StepName.FINANCES, activeUserUuid);
      funnelRemixP2
        ? navigate(APPLICATION_FULL_PATH)
        : navigate(`../${CONTINUE_ONLINE_TRAVEL_PATH}`);
    },
  });

  if (loading) {
    return <GlobalLoader />;
  }

  const onSubmit = async (values: FormValuesOutput<FormValues>) => {
    await createDataPointValuesFromInterview(values, schema);
  };

  return (
    <NavigatorContent>
      <DynamicFormWrapper>
        <DynamicForm<FormValues>
          name="finances"
          schema={schema}
          initialValues={data?.dataPointValues}
          onSubmit={onSubmit}
        />
      </DynamicFormWrapper>
    </NavigatorContent>
  );
};

export default Finances;
